import React from "react";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { MessageSquare, X, Check, Star } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateFeedback, useCreateFeedbackCycle } from "@/hooks/api-hooks";

const createFeedbackSchema = z.object({
  type: z.enum(['quick', 'cycle'], {
    errorMap: () => ({ message: 'Please select feedback type' })
  }),
  targetUserId: z.string().min(1, "Please select a recipient"),
  cycleName: z.string().optional(),
  cycleDescription: z.string().optional(),
  participants: z.array(z.string()).optional(),
  responseText: z.string().min(10, "Please provide meaningful feedback (at least 10 characters)"),
  rating: z.number().min(1).max(5).optional(),
  isAnonymous: z.boolean().default(false),
  isRecurring: z.boolean().default(false),
});

type CreateFeedbackFormData = z.infer<typeof createFeedbackSchema>;

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  jobTitle?: string;
}

interface CreateFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  users?: User[];
  onSuccess?: () => void;
}

export function CreateFeedbackModal({
  isOpen,
  onClose,
  users = [],
  onSuccess
}: CreateFeedbackModalProps) {
  const [selectedRating, setSelectedRating] = React.useState<number>(0);
  const createFeedbackMutation = useCreateFeedback();
  const createCycleMutation = useCreateFeedbackCycle();

  const form = useForm<CreateFeedbackFormData>({
    resolver: zodResolver(createFeedbackSchema),
    defaultValues: {
      type: 'quick',
      targetUserId: "",
      cycleName: "",
      cycleDescription: "",
      participants: [],
      responseText: "",
      rating: undefined,
      isAnonymous: false,
      isRecurring: false,
    },
  });

  const feedbackType = form.watch("type");
  const targetUserId = form.watch("targetUserId");
  const selectedUser = users.find(user => user.id === targetUserId);

  const onSubmit = async (data: CreateFeedbackFormData) => {
    try {
      if (data.type === 'cycle') {
        // Create feedback cycle
        const cycleData = {
          name: data.cycleName || "New Feedback Cycle",
          description: data.cycleDescription || "",
          targetUserId: data.targetUserId,
          participants: data.participants || [],
          questions: [
            { text: "Overall feedback", type: 'text' as const },
            { text: "Rating", type: 'rating' as const }
          ],
          isRecurring: data.isRecurring,
        };

        createCycleMutation.mutate(cycleData, {
          onSuccess: () => {
            toast.success('Feedback cycle created successfully!');
            onSuccess?.();
            onClose();
            form.reset();
            setSelectedRating(0);
          },
          onError: (error: any) => {
            console.error("Error creating feedback cycle:", error);
            toast.error(`Failed to create feedback cycle: ${error?.message || 'Please try again.'}`);
          }
        });
      } else {
        // Create quick feedback
        const feedbackData = {
          cycleId: 'quick-feedback', // Special ID for quick feedback
          targetUserId: data.targetUserId,
          responses: [
            { questionId: 'quick-feedback-text', text: data.responseText },
            ...(data.rating ? [{ questionId: 'quick-feedback-rating', text: data.rating.toString(), rating: data.rating }] : [])
          ],
          isAnonymous: data.isAnonymous,
        };

        createFeedbackMutation.mutate(feedbackData, {
          onSuccess: () => {
            toast.success('Feedback submitted successfully!');
            onSuccess?.();
            onClose();
            form.reset();
            setSelectedRating(0);
          },
          onError: (error: any) => {
            console.error("Error creating feedback:", error);
            toast.error(`Failed to submit feedback: ${error?.message || 'Please try again.'}`);
          }
        });
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  const handleClose = () => {
    form.reset();
    setSelectedRating(0);
    onClose();
  };

  const isPending = createFeedbackMutation.isPending || createCycleMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card text-card-foreground border border-border shadow-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold text-foreground">
              Give Feedback
            </DialogTitle>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Feedback Type */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Feedback Type *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select feedback type..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="quick">Quick Feedback</SelectItem>
                      <SelectItem value="cycle">Feedback Cycle</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {feedbackType === 'quick'
                      ? 'Send immediate feedback to a team member'
                      : 'Create a structured feedback cycle with multiple participants'
                    }
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Recipient Selection */}
            <FormField
              control={form.control}
              name="targetUserId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>To *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select recipient..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                              <AvatarFallback className="text-xs">
                                {user.firstName[0]}{user.lastName[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span>{user.firstName} {user.lastName}</span>
                            {user.jobTitle && (
                              <span className="text-xs text-muted-foreground">({user.jobTitle})</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Selected User Preview */}
            {selectedUser && (
              <div className="p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={selectedUser.avatar} alt={`${selectedUser.firstName} ${selectedUser.lastName}`} />
                    <AvatarFallback>
                      {selectedUser.firstName[0]}{selectedUser.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-foreground">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </p>
                    <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                    {selectedUser.jobTitle && (
                      <p className="text-sm text-muted-foreground">{selectedUser.jobTitle}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Cycle-specific fields */}
            {feedbackType === 'cycle' && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Cycle Details</h3>

                  <FormField
                    control={form.control}
                    name="cycleName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cycle Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Q1 Performance Review" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cycleDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe the purpose of this feedback cycle..."
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="isRecurring"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base font-medium">
                            Recurring Cycle
                          </FormLabel>
                          <p className="text-sm text-muted-foreground">
                            Make this a recurring feedback cycle
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                <Separator />
              </>
            )}

            {/* Feedback Content */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Your Feedback</h3>

              <FormField
                control={form.control}
                name="responseText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Feedback *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Share your feedback, observations, suggestions for improvement..."
                        rows={5}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Rating */}
              <div className="space-y-2">
                <FormLabel>Overall Rating (Optional)</FormLabel>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => {
                        setSelectedRating(rating);
                        form.setValue('rating', rating);
                      }}
                      className={`p-1 rounded transition-colors ${
                        rating <= selectedRating
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Star className={`h-6 w-6 ${rating <= selectedRating ? 'fill-current' : ''}`} />
                    </button>
                  ))}
                  {selectedRating > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedRating(0);
                        form.setValue('rating', undefined);
                      }}
                      className="ml-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {selectedRating > 0 && (
                  <p className="text-xs text-muted-foreground">
                    You rated: {selectedRating} star{selectedRating !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              <FormField
                control={form.control}
                name="isAnonymous"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base font-medium">
                        Anonymous Feedback
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Hide your identity when giving this feedback
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending}
                className="bg-primary hover:bg-primary/90"
              >
                {isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    {feedbackType === 'cycle' ? 'Creating...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {feedbackType === 'cycle' ? 'Create Cycle' : 'Submit Feedback'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
